'use client';

import { c as _c } from "react/compiler-runtime";
import type { MarketId, MarketParams } from '@morpho-org/blue-sdk';
import type { InterfaceToken, MarketForRewards } from '@morpho-org/dapp-core';
import { parseApiAsset } from '@morpho-org/dapp-core';
import { useEffect, useState } from 'react';
import type { MarketActionMode } from '@/hooks/operation/market/v2/marketAction.helpers';
import type { MarketGeoRestriction } from '@/hooks/useMarketGeoRestriction';
import { DISABLED_BORROW_TOOLTIP, DISABLED_LEND_TOOLTIP, DisabledActionTooltip } from '@/components/common/DisabledActionTooltip';
import { ErrorServiceUnavailable } from '@/components/common/Error';
import OperationModeButton from '@/components/SimpleActionPanel/OperationModeButton/OperationModeButton';
import { getParsedValue } from '@/helpers/formatters';
import { useMarketBlue } from '@/hooks/operation/market/v2/useMarketBlue';
import { useMarketHasPosition, useMarketPositionReadError } from '@/hooks/operation/market/v2/useMarketHasPosition';
import { useMarketHasSupplyPosition } from '@/hooks/operation/market/v2/useMarketHasSupplyPosition';
import { useMarketGeoRestriction } from '@/hooks/useMarketGeoRestriction';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { isMarketLendFlowEnabled } from '@/lib/shared/utils/flags.constants';
import { MarketOperationLend, MarketOperationLendWithdraw, MarketOperationRepayWithdraw, MarketOperationSupplyBorrow } from './MarketOperationPanel';
import { type ActionPanelState, toActionPanelMarket } from './toActionPanelMarket';
type TabAvailabilityState = boolean | undefined;
interface TabAvailability {
  canBorrow: TabAvailabilityState;
  canLend: TabAvailabilityState;
  canLendWithdraw: TabAvailabilityState;
  canRepayWithdraw: TabAvailabilityState;
  showLend: boolean;
}

/**
 * Blue permits both sides at once, but the panel's position preview assumes one, so the
 * opposite tab is disabled (not hidden) while a position is open.
 *
 * `hasBorrowPosition` / `hasLendPosition` are `undefined` while the fast position read is
 * still pending or has errored. An unresolved read is not a confirmed absence — so the
 * affected gates answer `undefined`: buttons stay disabled, but a mode the user already
 * chose is kept rather than bounced to the fallback.
 */
export const resolveTabAvailability = ({
  hasBorrowPosition,
  hasLendPosition,
  isLendEnabled,
  isLendPositionEnabled
}: {
  hasBorrowPosition: boolean | undefined;
  /** Withdrawable lend position: dust shares worth 0 assets must not count. */
  hasLendPosition: boolean | undefined;
  /** Both market-lend flags: gates the funds-moving supply entry. */
  isLendEnabled: boolean;
  /** `marketLendPosition` alone: the exit must survive the supply kill switch. */
  isLendPositionEnabled: boolean;
}): TabAvailability => ({
  canBorrow: hasBorrowPosition === true || hasLendPosition === false ? true : hasBorrowPosition === undefined || hasLendPosition === undefined ? undefined : false,
  canLend: !isLendEnabled ? false : hasBorrowPosition === undefined ? undefined : !hasBorrowPosition,
  canLendWithdraw: !isLendPositionEnabled ? false : hasLendPosition === undefined ? undefined : hasLendPosition === true,
  canRepayWithdraw: hasBorrowPosition,
  showLend: isLendEnabled
});
const getFallbackMode = (availability: TabAvailability): MarketActionMode => {
  if (availability.canBorrow === true) return 'supplyBorrow';
  if (availability.canLend === true) return 'lend';
  if (availability.canLendWithdraw === true) return 'lendWithdraw';
  if (availability.canRepayWithdraw === true) return 'repayWithdraw';
  return 'supplyBorrow';
};

/**
 * The gate backing a mode's tab. A record, not a ternary chain: growing the union fails to
 * compile here too, rather than falling through to whichever flag the last else named.
 */
export const getModeAvailability = (mode: MarketActionMode, availability: TabAvailability): TabAvailabilityState => ({
  supplyBorrow: availability.canBorrow,
  lend: availability.canLend,
  lendWithdraw: availability.canLendWithdraw,
  repayWithdraw: availability.canRepayWithdraw
})[mode];

/**
 * Keeps `mode` while its tab is available or still unresolved; falls back only once it is
 * known unavailable.
 */
export const resolveAvailableMode = (mode: MarketActionMode, availability: TabAvailability): MarketActionMode => getModeAvailability(mode, availability) === false ? getFallbackMode(availability) : mode;

/** A chosen tab stays mounted while its gate is unresolved, so the active button survives. */
const keepTabWhileUnresolved = (gate: TabAvailabilityState, isActive: boolean) => gate === true || gate === undefined && isActive;
interface MarketOperationContentProps {
  mode: MarketActionMode;
  state: ActionPanelState;
  marketId: MarketId;
  marketParams: MarketParams;
  marketForRewards: MarketForRewards;
  lltv: bigint;
  collateralAsset: InterfaceToken;
  loanAsset: InterfaceToken;
  geoRestriction: MarketGeoRestriction;
  isPositionGatePending: boolean;
}
const MarketOperationContent = t0 => {
  const $ = _c(23);
  const {
    mode,
    state,
    marketId,
    marketParams,
    marketForRewards,
    lltv,
    collateralAsset,
    loanAsset,
    geoRestriction,
    isPositionGatePending
  } = t0;
  let t1;
  if ($[0] !== collateralAsset || $[1] !== geoRestriction || $[2] !== isPositionGatePending || $[3] !== loanAsset || $[4] !== marketForRewards || $[5] !== marketId || $[6] !== marketParams) {
    t1 = {
      collateralAsset,
      loanAsset,
      marketId,
      marketParams,
      marketForRewards,
      geoRestriction,
      isPositionGatePending
    };
    $[0] = collateralAsset;
    $[1] = geoRestriction;
    $[2] = isPositionGatePending;
    $[3] = loanAsset;
    $[4] = marketForRewards;
    $[5] = marketId;
    $[6] = marketParams;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const shared = t1;
  switch (mode) {
    case "lend":
      {
        let t2;
        if ($[8] !== shared || $[9] !== state.supply.avgApy) {
          t2 = <MarketOperationLend {...shared} avgSupplyApy={state.supply.avgApy} />;
          $[8] = shared;
          $[9] = state.supply.avgApy;
          $[10] = t2;
        } else {
          t2 = $[10];
        }
        return t2;
      }
    case "lendWithdraw":
      {
        let t2;
        if ($[11] !== shared || $[12] !== state.supply.avgApy) {
          t2 = <MarketOperationLendWithdraw {...shared} avgSupplyApy={state.supply.avgApy} />;
          $[11] = shared;
          $[12] = state.supply.avgApy;
          $[13] = t2;
        } else {
          t2 = $[13];
        }
        return t2;
      }
    case "repayWithdraw":
      {
        let t2;
        if ($[14] !== lltv || $[15] !== shared || $[16] !== state.borrow.avgApy) {
          t2 = <MarketOperationRepayWithdraw {...shared} lltv={lltv} avgBorrowApy={state.borrow.avgApy} />;
          $[14] = lltv;
          $[15] = shared;
          $[16] = state.borrow.avgApy;
          $[17] = t2;
        } else {
          t2 = $[17];
        }
        return t2;
      }
    case "supplyBorrow":
      {
        let t2;
        if ($[18] !== lltv || $[19] !== shared || $[20] !== state.borrow.avgApy || $[21] !== state.collateralVault) {
          t2 = <MarketOperationSupplyBorrow {...shared} lltv={lltv} collateralVault={state.collateralVault} avgBorrowApy={state.borrow.avgApy} />;
          $[18] = lltv;
          $[19] = shared;
          $[20] = state.borrow.avgApy;
          $[21] = state.collateralVault;
          $[22] = t2;
        } else {
          t2 = $[22];
        }
        return t2;
      }
    default:
      {
        return mode satisfies never;
      }
  }
};
interface MarketSimpleActionPanelProps {
  marketId: MarketId;
  chainId: number;
  state: ActionPanelState;
}

/**
 * Market simple-action panel — fully morpho-sdk. Borrow/Repay and Lend/Withdraw all build,
 * simulate (evm-simulation, in the review dialog) and execute through morpho-sdk; the tab
 * gates read the position via `useMarketHasPosition` / `useMarketHasSupplyPosition`. No
 * simulation-sdk / `SimulationContext` involvement.
 */
const MarketSimpleActionPanel = ({
  marketId,
  chainId,
  state
}: MarketSimpleActionPanelProps) => {
  const flags = useFeatureFlags();
  // morpho-sdk MarketParams derived from the procedure's market — no simulation-sdk state needed.
  const market = toActionPanelMarket(state, chainId);
  const {
    marketParams
  } = market;
  const collateralAsset = getParsedValue(market.collateralAsset, parseApiAsset);
  const loanAsset = parseApiAsset(market.loanAsset);
  const geoRestriction = useMarketGeoRestriction({
    chainId,
    assetAddresses: [market.collateralAsset?.address, market.loanAsset.address]
  });
  const [mode, setMode] = useState<MarketActionMode>('supplyBorrow');

  // Gates the Repay / Withdraw tabs. Single `Morpho.position(marketId, …)` reads keyed only
  // on the `marketId` (known on mount), so the tabs can appear before the API market /
  // `MarketParams` resolve and without waiting for the heavier `getPositionData` multicall.
  const hasBorrowPosition = useMarketHasPosition(marketId);
  const hasPositionReadError = useMarketPositionReadError(marketId);
  const hasSupplyShares = useMarketHasSupplyPosition(marketId, chainId, flags.marketLendPosition);

  // A full assets-mode exit leaves rounding dust: shares that convert to 0 assets. Those
  // must not count as a lend position, or Borrow is disabled and pointed at a Withdraw the
  // form cannot build. Shares from the fast read stand in until the accrued position lands.
  const {
    positionData: lendPositionData
  } = useMarketBlue(marketParams);
  const hasLendPosition = hasSupplyShares && (lendPositionData ? lendPositionData.supplyAssets > 0n : true);
  const availability = resolveTabAvailability({
    hasBorrowPosition,
    hasLendPosition,
    isLendEnabled: isMarketLendFlowEnabled(flags),
    isLendPositionEnabled: flags.marketLendPosition
  });

  // Resolved synchronously so a mode whose tab just stopped being available (position
  // opened or closed) never paints its form for the frame before the effect corrects state.
  const resolvedMode = resolveAvailableMode(mode, availability);

  // The rendered form's own gate: `undefined` (unresolved read) or a `false` fallback both
  // block finalize — the tab may be the user's pick, not a confirmed-available one.
  const isSelectedGatePending = getModeAvailability(resolvedMode, availability) !== true;

  // Commit the resolved mode, so state and the rendered tab cannot drift apart.
  useEffect(() => {
    if (resolvedMode !== mode) setMode(resolvedMode);
  }, [mode, resolvedMode]);

  // Conditional tabs keep their active button mounted while the gate is unresolved.
  const showRepayWithdraw = keepTabWhileUnresolved(availability.canRepayWithdraw, resolvedMode === 'repayWithdraw');
  const showLendWithdraw = keepTabWhileUnresolved(availability.canLendWithdraw, resolvedMode === 'lendWithdraw');
  if (!collateralAsset || !loanAsset) return null;
  return <div className="gap-sm flex basis-[100%] flex-col" data-testid="market-action-panel">
      <div className="gap-2xs flex items-center justify-stretch">
        {/* A reason only once the other side is confirmed; an unsettled read blocks silently. */}
        <DisabledActionTooltip disabled={availability.canBorrow !== true} message={hasLendPosition === true ? DISABLED_BORROW_TOOLTIP : undefined}>
          <OperationModeButton dataTestId="supply-menu" isActive={resolvedMode === 'supplyBorrow'} onClick={() => setMode('supplyBorrow')} disabled={availability.canBorrow !== true} label="Borrow" />
        </DisabledActionTooltip>
        {showRepayWithdraw && <OperationModeButton dataTestId="repay-menu" isActive={resolvedMode === 'repayWithdraw'} onClick={() => setMode('repayWithdraw')} disabled={availability.canRepayWithdraw !== true} label="Repay" />}
        {availability.showLend && <DisabledActionTooltip disabled={availability.canLend !== true} message={hasBorrowPosition === true ? DISABLED_LEND_TOOLTIP : undefined}>
            <OperationModeButton dataTestId="lend-menu" isActive={resolvedMode === 'lend'} onClick={() => setMode('lend')} disabled={availability.canLend !== true} label="Lend" />
          </DisabledActionTooltip>}
        {showLendWithdraw && <OperationModeButton dataTestId="lend-withdraw-menu" isActive={resolvedMode === 'lendWithdraw'} onClick={() => setMode('lendWithdraw')} disabled={availability.canLendWithdraw !== true} label="Withdraw" />}
      </div>
      {hasPositionReadError && isSelectedGatePending ?
    // A failed read leaves the selected tab's gate unanswered for as long as it keeps
    // failing, so its form gives way to the error while any tab whose gate did answer
    // stays clickable.
    <ErrorServiceUnavailable minHeight="600px" /> :
    // While every gate is unresolved the chosen form still renders, so amounts typed
    // before connecting survive the reads restarting for the new account. Finalization is
    // blocked until its own gate answers: every form gets `isPositionGatePending` for the
    // selected mode's gate, on top of its own `isPositionLoading` read.
    <MarketOperationContent mode={resolvedMode} state={state} marketId={marketId} marketParams={marketParams} marketForRewards={market.marketForRewards} lltv={market.lltv} collateralAsset={collateralAsset} loanAsset={loanAsset} geoRestriction={geoRestriction} isPositionGatePending={isSelectedGatePending} />}
    </div>;
};
export default MarketSimpleActionPanel;